/* Keep optional photos attached: only submit once every selected upload is complete. */
(() => {
  'use strict';
  const initialized = new WeakSet();
  const text = {
    it: {"validating": "Stiamo verificando le tue foto. Attendi prima di aggiungere il prodotto al carrello.", "invalid": "Uno o più file non sono stati riconosciuti come foto e non sono stati caricati. Scegli un altro file JPG, PNG o WebP, oppure continua senza i file rifiutati e invia le foto via email dopo l’acquisto.", "acknowledge": "Continua senza i file rifiutati", "busy": "Le tue foto sono ancora in fase di caricamento. Attendi che siano tutte caricate prima di aggiungere il prodotto al carrello.", "retry": "Alcune foto non sono state caricate completamente. Riprova o rimuovi le foto incomplete prima di aggiungere il prodotto al carrello. Puoi anche rimuovere tutte le foto e inviarle via email dopo l’acquisto."},
    en: {
      validating: "Your photos are being checked. Please wait before adding to cart.",
      invalid: "One or more files could not be read as photos and were not uploaded. Choose a different JPG, PNG or WebP, or continue without the rejected files and email your photos after checkout.",
      acknowledge: "Continue without rejected files",
      busy: 'Your photos are still uploading. Please wait until every photo is complete before adding to cart.',
      retry: 'Some photos have not finished uploading. Retry or remove the incomplete photos before adding to cart. You can also remove all photos and email them after checkout.'
    },
    fr: {
      "validating": "Vos photos sont en cours de vérification. Veuillez patienter avant d’ajouter l’article au panier.",
      "invalid": "Un ou plusieurs fichiers n’ont pas pu être reconnus comme des photos et n’ont pas été téléversés. Choisissez d’autres fichiers JPG, PNG ou WebP, ou continuez sans les fichiers refusés et envoyez vos photos par e-mail après avoir passé commande.",
      "acknowledge": "Continuer sans les fichiers refusés",
      "busy": "Vos photos sont encore en cours de téléversement. Veuillez attendre la fin du téléversement de toutes les photos avant d’ajouter l’article au panier.",
      "retry": "Certaines photos n’ont pas été entièrement téléversées. Réessayez ou retirez les photos incomplètes avant d’ajouter l’article au panier. Vous pouvez aussi retirer toutes les photos et les envoyer par e-mail après avoir passé commande."
} ,
    de: {
      validating: "Deine Fotos werden geprüft. Bitte warte, bevor du den Artikel in den Warenkorb legst.",
      invalid: "Eine oder mehrere Dateien konnten nicht als Fotos gelesen werden und wurden nicht hochgeladen. Wähle andere JPG-, PNG- oder WebP-Dateien oder fahre ohne die abgelehnten Dateien fort und sende deine Fotos nach dem Kauf per E-Mail.",
      acknowledge: "Ohne abgelehnte Dateien fortfahren",
      busy: 'Deine Fotos werden noch hochgeladen. Bitte warte, bis alle Fotos vollständig hochgeladen sind, bevor du den Artikel in den Warenkorb legst.',
      retry: 'Einige Fotos wurden nicht vollständig hochgeladen. Bitte versuche es erneut oder entferne diese Fotos, bevor du den Artikel in den Warenkorb legst. Du kannst auch alle Fotos entfernen und sie nach dem Kauf per E-Mail senden.'
    },
    ja: {
      validating: "写真を確認しています。確認が終わるまでお待ちください。",
      invalid: "写真として読み込めないファイルがあり、アップロードされませんでした。別の JPG・PNG・WebP を選ぶか、読み込めなかったファイルを除いて進み、ご購入後に写真をメールでお送りください。",
      acknowledge: "読み込めないファイルを除いて進む",
      busy: '写真をアップロード中です。すべての写真のアップロードが完了してから、カートに追加してください。',
      retry: 'アップロードが完了していない写真があります。再試行するか、未完了の写真を削除してからカートに追加してください。写真をすべて削除し、ご購入後にメールで送ることもできます。'
    }
  };
  // Validate local photo bytes before the upload app starts transferring them.
  // HEIF/TIFF keep the app's existing conversion path; browser-decodable formats
  // must actually decode. The original file is never recompressed or replaced.
  const photoChecks = new WeakMap();
  let decoding = 0;
  const decodeQueue = [];
  async function decodePhoto(blob) {
    if (decoding >= 2) await new Promise(resolve => decodeQueue.push(resolve));
    decoding += 1;
    try {
      return await new Promise(resolve => {
        const image = new Image();
        const url = URL.createObjectURL(blob);
        let done = false;
        const finish = valid => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          image.onload = image.onerror = null;
          URL.revokeObjectURL(url);
          resolve(valid);
        };
        const timer = setTimeout(() => finish(false), 20000);
        image.onload = () => finish(image.naturalWidth > 0 && image.naturalHeight > 0);
        image.onerror = () => finish(false);
        image.src = url;
      });
    } finally {
      decoding -= 1;
      decodeQueue.shift()?.();
    }
  }
  async function readablePhoto(file) {
    if (!(file instanceof Blob) || file.size < 12) return false;
    if (photoChecks.has(file)) return photoChecks.get(file);
    const checking = (async () => {
      const bytes = new Uint8Array(await file.slice(0, 128).arrayBuffer());
      const ascii = (start, end) => String.fromCharCode(...bytes.slice(start, end));
      const png = bytes[0] === 137 && ascii(1, 4) === 'PNG' && bytes[4] === 13 && bytes[5] === 10 && bytes[6] === 26 && bytes[7] === 10;
      const jpeg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
      const gif = ['GIF87a', 'GIF89a'].includes(ascii(0, 6));
      const webp = ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP';
      const bmp = ascii(0, 2) === 'BM';
      const iso = ascii(4, 8) === 'ftyp';
      const brand = ascii(8, 12);
      const avif = iso && /avif|avis/.test(ascii(8, 64));
      if (png || jpeg || gif || webp || bmp || avif) return decodePhoto(file);
      // Structural checks only for formats the app can convert but some browsers
      // cannot decode. These are not claimed as full HEIF/TIFF content validation.
      if (iso && ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(brand)) {
        const view = new DataView(await file.arrayBuffer());
        let offset = 0, imageData = false, metadata = false;
        while (offset + 8 <= view.byteLength) {
          let length = view.getUint32(offset);
          const type = String.fromCharCode(...new Uint8Array(view.buffer, offset + 4, 4));
          if (length === 1) {
            if (offset + 16 > view.byteLength || view.getUint32(offset + 8) !== 0) return false;
            length = view.getUint32(offset + 12);
          } else if (length === 0) length = view.byteLength - offset;
          if (length < 8 || offset + length > view.byteLength) return false;
          if (type === 'mdat' && length > 8) imageData = true;
          if (type === 'meta' && length > 12) metadata = true;
          offset += length;
        }
        return offset === view.byteLength && imageData && metadata;
      }
      const tiff = (ascii(0, 2) === 'II' && bytes[2] === 42 && bytes[3] === 0) ||
        (ascii(0, 2) === 'MM' && bytes[2] === 0 && bytes[3] === 42);
      if (tiff) {
        const view = new DataView(bytes.buffer);
        const offset = view.getUint32(4, ascii(0, 2) === 'II');
        return offset >= 8 && offset + 2 < file.size;
      }
      return false;
    })().catch(() => false);
    photoChecks.set(file, checking);
    return checking;
  }

  function init(section) {
    if (!section || initialized.has(section)) return;
    initialized.add(section);
    const copy = text[document.documentElement.lang.split('-')[0]] || text.en;
    const ponds = new WeakMap();
    const buttonAttributes = new Map();
    const paymentInert = new Map();
    let choosing = false;
    let validating = 0;
    let rejected = false;
    const validationHooks = new WeakSet();
    let acknowledgeButton;
    let messageText;
    let message;
    let scheduled = false;
    function bindValidation(pond, root) {
      if (!pond || validationHooks.has(pond) || typeof pond.setOptions !== 'function') return;
      validationHooks.add(pond);
      const previous = pond.beforeAddFile;
      pond.setOptions({ beforeAddFile: async item => {
        validating += 1;
        choosing = false;
        refresh();
        try {
          if (typeof previous === 'function' && await previous.call(pond, item) === false) return false;
          const valid = await readablePhoto(item.file);
          if (!valid) rejected = true;
          return valid;
        } catch (_) {
          rejected = true;
          return false;
        } finally {
          validating -= 1;
          choosing = false;
          schedule();
        }
      }});
      root.dataset.dmPhotoValidation = 'active';
    }
    function state() {
      if (validating > 0) return 'validating';
      if (rejected) return 'invalid';
      const states = [];
      section.querySelectorAll('.filepond--root').forEach(root => {
        const pond = ponds.get(root);
        if (pond && typeof pond.getFiles === 'function') {
          // FilePond's public FileStatus enum: 5 is PROCESSING_COMPLETE.
          pond.getFiles().forEach(file => states.push(file.status === 5 ? 'ready' : [2, 6, 8, 10].includes(file.status) ? 'retry' : 'busy'));
        } else {
          root.querySelectorAll('.filepond--item').forEach(item => {
            const status = item.dataset.filepondItemState || '';
            states.push(status === 'processing-complete' ? 'ready' : /error|idle/.test(status) ? 'retry' : 'busy');
          });
        }
      });
      return states.includes('retry') ? 'retry' : choosing || states.includes('busy') ? 'busy' : 'ready';
    }
    function refresh() {
      const current = state();
      const blocked = current !== 'ready';
      section.dataset.dmUploadState = current;
      const uploader = section.querySelector('.cl-upload--wrapper, .filepond--root');
      if (!message && uploader) {
        message = document.createElement('div');
        message.className = 'dm-upload-guard-message';
        message.setAttribute('role', 'status');
        message.setAttribute('aria-live', 'polite');
        message.tabIndex = -1;
        message.style.cssText = 'margin:12px 0;padding:14px 16px;border:1px solid #b27422;border-radius:8px;background:#fff5e6;color:#52350e;font-size:14px;line-height:1.55;text-align:left;';
        messageText = document.createElement('p');
        messageText.style.margin = '0';
        acknowledgeButton = document.createElement('button');
        acknowledgeButton.type = 'button';
        acknowledgeButton.textContent = copy.acknowledge;
        acknowledgeButton.style.cssText = 'display:block;margin-top:10px;padding:8px 12px;border:1px solid currentColor;border-radius:6px;background:transparent;color:inherit;font:inherit;cursor:pointer';
        acknowledgeButton.addEventListener('click', () => { rejected = false; refresh(); });
        message.append(messageText, acknowledgeButton);
        uploader.after(message);
      }
      if (message) {
        message.hidden = !blocked;
        const value = blocked ? copy[current] : '';
        if (messageText.textContent !== value) messageText.textContent = value;
        acknowledgeButton.hidden = current !== 'invalid';
        acknowledgeButton.style.display = current === 'invalid' ? 'block' : 'none';
      }
      section.querySelectorAll('form[action*="/cart/add"] [type="submit"]').forEach(button => {
        if (blocked) {
          if (!buttonAttributes.has(button)) buttonAttributes.set(button, button.getAttribute('aria-disabled'));
          button.setAttribute('aria-disabled', 'true');
        } else if (buttonAttributes.has(button)) {
          const old = buttonAttributes.get(button);
          if (old === null) button.removeAttribute('aria-disabled'); else button.setAttribute('aria-disabled', old);
          buttonAttributes.delete(button);
        }
      });
      section.querySelectorAll('.shopify-payment-button').forEach(payment => {
        if (blocked) {
          if (!paymentInert.has(payment)) paymentInert.set(payment, payment.inert);
          payment.inert = true;
        } else if (paymentInert.has(payment)) {
          payment.inert = paymentInert.get(payment);
          paymentInert.delete(payment);
        }
      });
      return current;
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => { scheduled = false; refresh(); });
    }
    function block(event) {
      if (refresh() === 'ready') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (message) { message.focus({ preventScroll: true }); message.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
    }
    // Capture before the theme's AJAX cart and the add-on bundle submit handler.
    document.addEventListener('click', event => {
      const control = event.target instanceof Element ? event.target.closest('button, input[type="submit"], .shopify-payment-button') : null;
      if (!control || !section.contains(control)) return;
      if (control.closest('.shopify-payment-button') || (control.type === 'submit' && control.form?.getAttribute('action')?.includes('/cart/add'))) block(event);
    }, true);
    document.addEventListener('submit', event => {
      if (section.contains(event.target) && event.target.getAttribute('action')?.includes('/cart/add')) block(event);
    }, true);
    section.addEventListener('change', event => {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.type === 'file' && input.closest('.filepond--root') && input.files?.length && input.files.length <= 10) { choosing = true; refresh(); }
    }, true);
    section.addEventListener('drop', event => {
      if (event.target instanceof Element && event.target.closest('.filepond--root') && event.dataTransfer?.files.length) { choosing = true; refresh(); }
    }, true);
    const events = ['init', 'initfile', 'addfilestart', 'addfile', 'processfilestart', 'processfile', 'processfileabort', 'processfilerevert', 'removefile', 'updatefiles', 'warning', 'error'];
    events.forEach(name => section.addEventListener('FilePond:' + name, event => {
      const root = event.target instanceof Element ? event.target.closest('.filepond--root') : null;
      if (root && event.detail?.pond) {
        ponds.set(root, event.detail.pond);
        bindValidation(event.detail.pond, root);
      }
      choosing = false;
      schedule();
    }, true));
    new MutationObserver(schedule).observe(section, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-filepond-item-state'] });
    section.querySelectorAll('.filepond--root').forEach(root => {
      const pond = window.FilePond?.find?.(root);
      if (pond) { ponds.set(root, pond); bindValidation(pond, root); }
    });
    refresh();
  }
  document.querySelectorAll('script[data-dm-upload-guard]').forEach(script => init(document.getElementById(script.dataset.dmUploadGuard)));
})();
